import * as React from 'react';
import { useContext } from 'react';

import type { ApolloError } from '@apollo/client';

import type { DegreePageHeader_MyPageHeaderFragment as MyPageHeaderType } from 'bundles/composable-page-header-degree/queries/__generated__/DegreeMyPageHeaderQuery';

export type DegreeMyPageHeaderContextType = {
  myPageHeader?: MyPageHeaderType;
  hasEnrolledPrograms?: boolean;
  // To be used to indicate to page header renderers that we may want to render a fallback header as the call to get the header data failed if this is defined.
  error?: ApolloError;
};

const DegreeMyPageHeaderContext = React.createContext<DegreeMyPageHeaderContextType | undefined>(undefined);

// Created to ensure the page header BFF and the backup made via old queries are consistent
export type UseDegreeMyPageHeaderResult = {
  naptimeChosenOrganization?: MyPageHeaderType['naptimeChosenOrganization'];
  naptimeOrganizationsWithProgramEnrollments?: MyPageHeaderType['naptimeOrganizationsWithProgramEnrollments'];
  enrolledDegreePrograms?: MyPageHeaderType['enrolledDegreePrograms'];
  enrolledEnterprisePrograms?: MyPageHeaderType['enrolledEnterprisePrograms'];
  chosenEnterpriseProgram?: MyPageHeaderType['chosenEnterpriseProgram'];
  chosenDegree?: MyPageHeaderType['chosenDegree'];
  hasCheatingIncidents?: MyPageHeaderType['hasCheatingIncidents'];
  canViewEducatorAdminLink?: MyPageHeaderType['canViewEducatorAdminLink'];
  isCourseraPlusLearner?: MyPageHeaderType['isCourseraPlusLearner'];
  courseraPlusSubscriptionCycle?: MyPageHeaderType['courseraPlusSubscriptionCycle'];
  naptimeManagedThirdPartyOrganizations?: MyPageHeaderType['naptimeManagedThirdPartyOrganizations'];
  hasEnrolledPrograms?: boolean | null;
};

export const useDegreeMyPageHeader = (): UseDegreeMyPageHeaderResult => {
  const context = useContext(DegreeMyPageHeaderContext);

  if (!context) {
    throw new Error('useDegreeMyPageHeader must be used within an DegreeMyPageHeaderContext.Provider');
  }

  const { myPageHeader } = context;

  const enrolledDegreePrograms = myPageHeader?.enrolledDegreePrograms;
  const enrolledEnterprisePrograms = myPageHeader?.enrolledEnterprisePrograms;
  const enrolledOrganizations = myPageHeader?.naptimeOrganizationsWithProgramEnrollments;
  const hasEnrolledPrograms =
    (enrolledDegreePrograms && enrolledDegreePrograms.length > 0) ||
    (enrolledEnterprisePrograms && enrolledEnterprisePrograms.length > 0) ||
    (enrolledOrganizations && enrolledOrganizations.length > 0);

  return {
    naptimeChosenOrganization: myPageHeader?.naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments: enrolledOrganizations,
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenEnterpriseProgram: myPageHeader?.chosenEnterpriseProgram,
    chosenDegree: myPageHeader?.chosenDegree,
    hasCheatingIncidents: myPageHeader?.hasCheatingIncidents,
    canViewEducatorAdminLink: myPageHeader?.canViewEducatorAdminLink,
    isCourseraPlusLearner: myPageHeader?.isCourseraPlusLearner,
    courseraPlusSubscriptionCycle: myPageHeader?.courseraPlusSubscriptionCycle,
    naptimeManagedThirdPartyOrganizations: myPageHeader?.naptimeManagedThirdPartyOrganizations,
    hasEnrolledPrograms,
  };
};

export const useDegreeMyPageHeaderError = () => {
  const context = useContext(DegreeMyPageHeaderContext);

  return context?.error;
};

export default DegreeMyPageHeaderContext;
